import React, { ReactNode, useContext, useState } from "react";
import { Box, Grid } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import useEnv from "../../../../hooks/env";
import ErrorSnackbarContext from "../../../../contexts/ErrorSnackbarContext";
import LaboratoriesApi from "../../../../api/services/LaboratoriesApi";
import ManagedLaboratoryResponse from "../../../../api/models/test-request/ManagedLaboratoryResponse";
import confirmationDialogConfig from "../../constants/ConfirmationDialogConfig";
import ConfirmationDialogContext from "../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { NavigationContext } from "../../../../contexts/NavigationContext";
import AssignableUsersInput from "../AssignableUsersInput";
import CustomButton from "../../../../components/Button/CustomButton";

export interface EditLabRelationsFormProps {
  formRef: React.RefObject<
    FormikProps<{
      users: {
        id: number;
        name: string;
      }[];
    }>
  >;
  laboratoryId: number;
  assignedUsers: ManagedLaboratoryResponse["assignedUsers"];
  tabEdited: boolean;
  handleTabEdited: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditLabRelationsForm: React.FC<EditLabRelationsFormProps> = (props) => {
  const { api } = useEnv();
  const laboratoriesApi = new LaboratoriesApi(api);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { dirtyFlag, updateDirtyFlag, updatePreventNav } = useContext(NavigationContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const [reset, setReset] = useState(false);

  const { laboratoryId, assignedUsers, formRef, handleTabEdited } = props;

  const [initialAssignedUsers, setInitialAssignedUsers] = useState(assignedUsers);

  const handleUpdatePreventNav = () => {
    return showConfirmationDialog(confirmationDialogConfig).then((data: string) => {
      if (formRef && formRef.current) {
        if (data === "DEFAULT") {
          formRef.current?.submitForm();
        } else {
          formRef.current.setFieldValue("users", formRef.current.initialValues.users);
          updateDirtyFlag(false);
        }
      }
    });
  };

  const handleSubmit = (values: { users: TestRequestRaw.Schemas.LaboratoryUserResponse[] }) => {
    const assignedUserIds = values.users.map((user) => user.id);
    laboratoriesApi
      .editLaboratoryRelations(laboratoryId, { id: laboratoryId, assignedUserIds })
      .then(() => showSnackbar("Assigned users updated", "success"))
      .then(() => handleTabEdited(true))
      .then(() => updateDirtyFlag(false))
      .then(() => setInitialAssignedUsers(values.users))
      .catch(handleHttpError);
  };

  const handleRevert = () => {
    updatePreventNav(handleUpdatePreventNav);
    if (formRef && formRef.current) {
      formRef.current.setFieldValue("users", formRef.current.initialValues.users);
      updateDirtyFlag(false);
    }
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={{ users: initialAssignedUsers }}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(formikProps: FormikProps<{ users: { id: number; name: string }[] }>): ReactNode => {
          return (
            <Form>
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <AssignableUsersInput
                    formRef={formRef}
                    disabled={false}
                    laboratoryId={laboratoryId}
                    reset={reset}
                    canPreventNavigation
                    handleSetReseting={setReset}
                    {...formikProps}
                  />
                </Grid>
                <Grid container item direction="row" justify="flex-start">
                  <Box marginRight={2}>
                    <CustomButton text="Revert" disabled={!dirtyFlag} onClick={handleRevert} />
                  </Box>
                  <Box>
                    <CustomButton primary text="Save" type="submit" />
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default EditLabRelationsForm;
